import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import CarouselItem from './CarouselItem';
import { clsx } from 'clsx';
import { Navigation } from 'swiper';
import { ModalWrapper, PortalModal, useParameters } from '@web/utils/src';
import arrowIcon from '@web/utils/src/assets/icons/functions/arrow.svg';
import useOrderAgain from '@/hooks/useOrderAgain';
import useUserLineItemsPagination from '@/hooks/useUserLineItemsPagination';
import 'swiper/css';

const InventoryCarousel = () => {
  const { result } = useUserLineItemsPagination(20);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(true);
  const navigationNextRef = useRef(null);
  const navigationPrevRef = useRef(null);

  //
  const swiperOptions = {
    modules: [Navigation],
    spaceBetween: 20,
    slidesPerView: 1,
    breakpoints: {
      768: {
        slidesPerView: 2,
      },
      1024: {
        slidesPerView: 2.25,
      },
    },
    navigation: {
      prevEl: '.inventory-carousel .swiper-button-prev',
      nextEl: '.inventory-carousel .swiper-button-next',
    },
    onReachEnd: () => setNextBtnEnabled(false),
    onReachBeginning: () => setPrevBtnEnabled(false),
    onSlidePrevTransitionEnd: () => setNextBtnEnabled(true),
    onSlideNextTransitionEnd: () => setPrevBtnEnabled(true),
  };
  //

  const {
    handleOrderAgain,
    warningModalOpen,
    setWarningModalOpen,
    handleSubmitWarningModal,
    handleCancelWarningModal,
  } = useOrderAgain();
  const handleClick = (lineItem) => {
    navigate(`/documents/${lineItem.retailerID}/orders/${lineItem.order.id}`);
  };

  return (
    <>
      {warningModalOpen && (
        <PortalModal isOpen={warningModalOpen} setIsOpen={setWarningModalOpen}>
          <ModalWrapper
            setIsOpen={setWarningModalOpen}
            submitText={'Nur diesen Artikel bestellen'}
            cancelText={'Zu bestehenden Artikeln hinzufügen'}
            t={t}
            submitHandler={handleSubmitWarningModal}
            cancelHandler={handleCancelWarningModal}
          >
            <p className="py-2-0">
              Sie haben bereits Artikel im Warenkorb
              <br />
              Wollen Sie nur diesen Artikel bestellen oder ihn zu den
              bestehenden Artikeln hinzufügen?
            </p>
          </ModalWrapper>
        </PortalModal>
      )}
      <div className="py-2-0 overflow-visible inventory-carousel relative">
        <div className="flex items-center">
          <p className="font-bd text-3-0 md:text-2-0">
            {t('retailer.carousel_title')}
          </p>
          <div className="ml-auto flex gap-1-0">
            <div
              className={clsx(
                prevBtnEnabled ? 'bg-primary-black' : 'bg-primary-grey',
                'w-5-0 h-5-0  md:w-3-0 md:h-3-0 rounded-base  p-1-0 cusror-pointer swiper-button-prev transition-colors duration-200 ease-in cursor-pointer',
              )}
              ref={navigationPrevRef}
            >
              <img
                src={arrowIcon}
                className="w-full h-full transform rotate-180"
              />
            </div>
            <div
              className={clsx(
                nextBtnEnabled ? 'bg-primary-black' : 'bg-primary-grey',
                'w-5-0 h-5-0  md:w-3-0 md:h-3-0 rounded-base  p-1-0 cusror-pointer swiper-button-next transition-colors duration-200 ease-in cursor-pointer',
              )}
              ref={navigationNextRef}
            >
              <img src={arrowIcon} className="w-full h-full" />
            </div>
          </div>
        </div>
        <div className="py-3-0 md:py-2-0 swiper-container min-h-[12rem] right-side-fade">
          {result && (
            <Swiper {...swiperOptions}>
              {result.map((article) => (
                <SwiperSlide key={article.id}>
                  <CarouselItem
                    key={article.id}
                    lineItem={article}
                    handleClick={handleClick}
                    handleOrderAgain={handleOrderAgain}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </div>
    </>
  );
};

export default InventoryCarousel;
